import { memo, useState } from 'react';

interface ImageCellProps {
  src: string | null | undefined;
  alt?: string;
  size?: 'sm' | 'md' | 'lg' | 'fill';
  shape?: 'square' | 'rounded' | 'circle';
  fallback?: string;
}

const SIZE_CONFIG = {
  sm: { width: 32, height: 32 },
  md: { width: 40, height: 40 },
  lg: { width: 56, height: 56 },
  fill: { width: '100%', height: '100%' }
} as const;

const SHAPE_CONFIG = {
  square: 'var(--radius-sm)',
  rounded: 'var(--radius-md)',
  circle: '50%'
};

// Default fallback - product placeholder icon
const DefaultFallback = ({ size }: { size: 'sm' | 'md' | 'lg' | 'fill' }) => {
  const { width, height } = SIZE_CONFIG[size];
  const iconSize = size === 'sm' ? 14 : size === 'md' ? 18 : size === 'fill' ? 24 : 24;

  return (
    <div
      style={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-neutral-100)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--color-neutral-400)'
      }}
    >
      <svg
        width={iconSize}
        height={iconSize}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    </div>
  );
};

export const ImageCell = memo(({
  src,
  alt = 'Product image',
  size = 'md',
  shape = 'rounded',
  fallback
}: ImageCellProps) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { width, height } = SIZE_CONFIG[size];
  const borderRadius = SHAPE_CONFIG[shape];
  const isFill = size === 'fill';

  // Fill mode: expand to fill most of the cell with small padding
  const containerStyle: React.CSSProperties = isFill
    ? {
        margin: '-4px -6px',
        width: 'calc(100% + 12px)',
        height: 'calc(100% + 8px)',
        minHeight: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }
    : {
        position: 'relative',
        width,
        height
      };

  const imgStyle: React.CSSProperties = isFill
    ? {
        width: '100%',
        height: '100%',
        minHeight: '40px',
        objectFit: 'cover',
        borderRadius: 'var(--radius-sm)'
      }
    : {
        width,
        height,
        objectFit: 'cover',
        borderRadius,
        border: '1px solid var(--color-border-light)'
      };

  if (!src || hasError) {
    if (fallback) {
      return (
        <div style={containerStyle}>
          <img
            src={fallback}
            alt={alt}
            style={{
              ...imgStyle,
              opacity: 1
            }}
          />
        </div>
      );
    }
    return (
      <div style={containerStyle}>
        <DefaultFallback size={size} />
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-neutral-100)',
            borderRadius: isFill ? 0 : borderRadius,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}
        />
      )}
      <img
        src={src}
        alt={alt}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setHasError(true);
          setIsLoading(false);
        }}
        style={{
          ...imgStyle,
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.2s ease'
        }}
      />
    </div>
  );
});

ImageCell.displayName = 'ImageCell';
